import type { Attachment } from '@core/attachments-types';
import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import type {
  Admin,
  InterviewResultAnswerOption,
  RatingLevelType,
} from '@core/interview-results-types';
import type { QuestionKind, QuestionOptions } from '@core/interview-templates-types';
import type {
  JobSkillsSummaryDetailsBasicInfo,
  PositionSkill,
} from '@core/positions-and-skills-types';
import type { EditorJSONState } from '@core/prosemirror-types';
import type { Stage } from '@core/stages-types';

import type { AggregatedEvaluationsSkill } from './aggregated-evaluations-skills.types';

export enum AggregatedEvaluationsGroupBy {
  Question = 'question',
  Reviewer = 'reviewer',
}

export interface AggregatedEvaluationsParams {
  candidateId: number;
  groupBy: AggregatedEvaluationsGroupBy;
  offerId?: number;
  talentPoolId?: number;
  withoutOffer?: boolean;
}

export interface AggregatedEvaluationsLastEvaluation {
  evaluatedAt: string;
  stage: Pick<Stage, 'id' | 'name'> | null;
}

export interface AggregatedEvaluationsRating {
  score: number;
  answerCount: number;
  reviewers: AggregatedEvaluationsReviewer[];
}

export interface AggregatedEvaluationsScore {
  average: number;
  ratings: AggregatedEvaluationsRating[];
}

export interface AggregatedEvaluationsSummary {
  evaluationsCount: number;
  reviewersCount: number;
  reviewers: AggregatedEvaluationsReviewer[];
  lastEvaluation: AggregatedEvaluationsLastEvaluation | null;
  score: AggregatedEvaluationsScore;
  skills: JobSkillsSummaryDetailsBasicInfo | null;
  hasHiddenEvaluations: boolean;
}

export enum AggregatedEvaluationsFormKind {
  Template = 'template',
  QuickEvaluation = 'quick_evaluation',
}

export interface AggregatedEvaluationsFormBase {
  kind: AggregatedEvaluationsFormKind;
  interviewTemplateName: string | null;
  description: string;
  evaluationsCount: number;
  reviewersCount: number;
  hasHiddenEvaluations: boolean;
  hiddenEvaluationsCount: number;
  hiddenAdminIds: number[];
  hiddenGuestIds: number[];
}

export enum AggregatedEvaluationsQuestionAggregateType {
  Attachments = 'attachments',
  BooleanDistribution = 'boolean_distribution',
  OptionsDistribution = 'options_distribution',
  Responses = 'responses',
  Skills = 'skills',
}

export interface AggregatedEvaluationsQuestionResponse {
  adminId: number;
  content: string;
  resultId: number;
  questionOptions?: QuestionOptions;
}

export interface AggregatedEvaluationsResponsesAggregate {
  type: AggregatedEvaluationsQuestionAggregateType.Responses;
  responses: AggregatedEvaluationsQuestionResponse[];
}

export interface AggregatedEvaluationsQuestionOption {
  count: number;
  title: string;
}

export interface AggregatedEvaluationsOptionsDistributionAggregate {
  type: AggregatedEvaluationsQuestionAggregateType.OptionsDistribution;
  options: AggregatedEvaluationsQuestionOption[];
}

export interface AggregatedEvaluationsBooleanDistributionAggregate {
  type: AggregatedEvaluationsQuestionAggregateType.BooleanDistribution;
  yes: number;
  no: number;
}

export type AggregatedEvaluationsAttachment = Omit<Attachment, 'admin' | 'private'> & {
  admin: AggregatedEvaluationsAdmin;
};

export interface AggregatedEvaluationsAttachmentsAggregate {
  type: AggregatedEvaluationsQuestionAggregateType.Attachments;
  attachments: AggregatedEvaluationsAttachment[];
}

export interface AggregatedEvaluationsSkillsAggregate {
  type: AggregatedEvaluationsQuestionAggregateType.Skills;
  skills: AggregatedEvaluationsSkill[];
}

export type AggregatedEvaluationsQuestionAggregate =
  | AggregatedEvaluationsResponsesAggregate
  | AggregatedEvaluationsOptionsDistributionAggregate
  | AggregatedEvaluationsBooleanDistributionAggregate
  | AggregatedEvaluationsAttachmentsAggregate
  | AggregatedEvaluationsSkillsAggregate;

export interface AggregatedEvaluationsQuestionNote {
  admin: AggregatedEvaluationsAdmin;
  noteHtml: string;
  noteJson: EditorJSONState;
}

export interface AggregatedEvaluationsQuestion {
  questionHash: string;
  questionKind: QuestionKind;
  questionOptions?: QuestionOptions;
  questionTitle: string | null;
  questionRequired: boolean;
  answersCount: number;
  aggregate: AggregatedEvaluationsQuestionAggregate | null;
  notes: AggregatedEvaluationsQuestionNote[];
}

export interface AggregatedEvaluationsAnswer {
  id: number;
  content?: string | null;
  contentHtml?: string | null;
  contentJson?: EditorJSONState | null;
  contents?: string[];
  options?: InterviewResultAnswerOption[];
  attachmentIds?: number[];
  attachments?: AggregatedEvaluationsAttachment[];
  skillRatings?: Array<{ skillId: number; proficiencyId: number | null }>;
  noteHtml: string | null;
  noteJson: EditorJSONState | null;
  questionHint: string | null;
  questionKind: QuestionKind;
  questionOptions: QuestionOptions & { skills?: PositionSkill[] };
  questionRequired: boolean;
  questionTitle: string;
}

export type AggregatedEvaluationsAdmin = Pick<
  Admin,
  'hasAvatar' | 'id' | 'initials' | 'name' | 'photoThumbUrl'
>;

export interface AggregatedEvaluationsReviewer extends AggregatedEvaluationsAdmin {
  type: Admin['type'];
  isHiringManager: boolean;
  isRecruiter: boolean;
}

export interface AggregatedEvaluationsReviewerEvaluation {
  resultId: number;
  reviewer: AggregatedEvaluationsReviewer;
  answers: AggregatedEvaluationsAnswer[];
  createdAt: string;
  updatedAt: string;
  rating: RatingLevelType;
  ratingNote: string | null;
  ratingScale: RatingScale | null;
  score: number;
  skillsCount: number;
  matchingSkillsCount: number;
  canEdit?: boolean;
  canDelete?: boolean;
}

export interface AggregatedEvaluationsStage {
  stage: Pick<Stage, 'id' | 'name'> | null;
  reviewers: AggregatedEvaluationsReviewerEvaluation[];
}

export interface QuestionGroupedForm extends AggregatedEvaluationsFormBase {
  questions: AggregatedEvaluationsQuestion[];
}

export interface ReviewerGroupedForm extends AggregatedEvaluationsFormBase {
  stages: AggregatedEvaluationsStage[];
}

interface AggregatedEvaluationsBase {
  id: string;
  evaluationsCount: number;
  summary: AggregatedEvaluationsSummary;
}

export interface QuestionAggregatedEvaluations extends AggregatedEvaluationsBase {
  groupBy: AggregatedEvaluationsGroupBy.Question;
  forms: QuestionGroupedForm[];
}

export interface ReviewerAggregatedEvaluations extends AggregatedEvaluationsBase {
  groupBy: AggregatedEvaluationsGroupBy.Reviewer;
  forms: ReviewerGroupedForm[];
}

export type AggregatedEvaluations = QuestionAggregatedEvaluations | ReviewerAggregatedEvaluations;

export type AggregatedEvaluationsResponse =
  | Omit<QuestionAggregatedEvaluations, 'id'>
  | Omit<ReviewerAggregatedEvaluations, 'id'>;
